import { useState, type ReactNode } from 'react';
import type { ModuleDefinition, ReleaseFlag } from '@traceio-org/platform-contracts';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../components/feedback';
import { RadioGroup, ReasonField, TextAreaField, reasonError } from '../components/fields';
import { DateTimeText, ScrollTable } from '../components/records';
import { WriteDialog, useDialogTarget } from '../components/WriteDialog';
import { useAbilities, useAdmin } from '../context';
import { ENVIRONMENT_LABELS, RELEASE_STATUS_LABELS, describeRollout, productName } from '../format';
import { useAdminRead } from '../useAdminRead';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_ORGANIZATIONS = 1000;

type RolloutKind = ReleaseFlag['rollout']['kind'];

const ROLLOUT_CHOICES = [
  {
    value: 'all',
    label: 'Every organization',
    description: 'Released to every organization that bought it.',
  },
  {
    value: 'organizations',
    label: 'Only the organizations listed',
    description: 'A pilot: released to the organizations you list, by ID, and nobody else.',
  },
  { value: 'off', label: 'Off', description: 'Released to no organization, whatever its catalog status.' },
] as const;

/** One module's rollout in an environment, as the table and the dialog read it. */
interface FlagRow {
  module: Pick<ModuleDefinition, 'key' | 'name' | 'productKey' | 'releaseStatus'> | null;
  moduleKey: string;
  flag: ReleaseFlag | null;
}

/** Organization IDs typed one per line (or separated by commas or spaces), without duplicates. */
export function parseOrganizationIds(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  ];
}

function RolloutText({ row }: { row: FlagRow }) {
  if (!row.flag) {
    const ga = row.module?.releaseStatus === 'ga';
    return (
      <span className="admin-muted">
        No flag: {ga ? 'every organization (generally available)' : 'off until a flag releases it'}
      </span>
    );
  }
  const rollout = row.flag.rollout;
  if (rollout.kind !== 'organizations') return <>{describeRollout(rollout)}</>;
  return (
    <details className="admin-details">
      <summary>{describeRollout(rollout)}</summary>
      <ul className="admin-plain-list">
        {rollout.organizationIds.map((id) => (
          <li key={id}>
            <code>{id}</code>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Release flags (/release-flags): whether each module of the catalog is released in this environment, and to
 * whom: every organization, a list of organizations (a pilot) or none. Without a flag a module is released
 * where its catalog status is generally available, and nowhere else. Platform admins change a rollout with a
 * reason; platform-api sets flags of its own environment only.
 */
export function ReleaseFlagsPage() {
  const { api, environment } = useAdmin();
  const abilities = useAbilities();
  const flagsRead = useAdminRead('release-flags', (signal) => api.listReleaseFlags(signal));
  const catalogRead = useAdminRead('module-catalog', (signal) => api.listModuleCatalog(signal));
  const flags = flagsRead.failure ? null : (flagsRead.data?.items ?? null);
  const catalog = catalogRead.failure ? null : (catalogRead.data?.modules ?? null);
  const environmentLabel = ENVIRONMENT_LABELS[environment];

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<FlagRow>();
  const [kind, setKind] = useState<RolloutKind>('all');
  const [ids, setIds] = useState('');
  const [idsProblem, setIdsProblem] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);

  const here = (flags ?? []).filter((flag) => flag.environment === environment);
  const elsewhere = (flags ?? []).filter((flag) => flag.environment !== environment);
  // One row per module of the catalog; flags for modules the catalog no longer has stay visible too.
  const rows: FlagRow[] = [
    ...(catalog ?? []).map((module) => ({
      module,
      moduleKey: module.key,
      flag: here.find((flag) => flag.moduleKey === module.key) ?? null,
    })),
    ...here
      .filter((flag) => !(catalog ?? []).some((module) => module.key === flag.moduleKey))
      .map((flag) => ({ module: null, moduleKey: flag.moduleKey, flag })),
  ];

  const open = (row: FlagRow) => {
    const rollout = row.flag?.rollout;
    setKind(rollout?.kind ?? 'all');
    setIds(rollout?.kind === 'organizations' ? rollout.organizationIds.join('\n') : '');
    setIdsProblem(null);
    setReason('');
    setReasonProblem(null);
    dialog.show(row);
  };

  const target = dialog.target;
  const targetName = target ? (target.module?.name ?? target.moduleKey) : '';
  let dialogView: ReactNode = null;
  if (target) {
    dialogView = (
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={`Release ${targetName} in ${environmentLabel}`}
        description="The change applies at once to every new piece of work."
        submitLabel="Save the rollout"
        onSubmit={async () => {
          const list = parseOrganizationIds(ids);
          let problem: string | null = null;
          if (kind === 'organizations') {
            if (list.length === 0) problem = 'List at least one organization ID.';
            else if (list.length > MAX_ORGANIZATIONS)
              problem = `List at most ${MAX_ORGANIZATIONS} organizations.`;
            else if (list.some((id) => !UUID.test(id))) {
              problem = `These are not organization IDs: ${list.filter((id) => !UUID.test(id)).join(', ')}.`;
            }
          }
          setIdsProblem(problem);
          const reasonIssue = reasonError(reason);
          setReasonProblem(reasonIssue);
          if (problem || reasonIssue) return 'invalid';
          await api.setReleaseFlag({
            moduleKey: target.moduleKey,
            environment,
            rollout:
              kind === 'organizations'
                ? { kind: 'organizations', organizationIds: list }
                : kind === 'all'
                  ? { kind: 'all' }
                  : { kind: 'off' },
            reason,
          });
          return undefined;
        }}
        onSaved={() => {
          const summary =
            kind === 'organizations'
              ? describeRollout({ kind, organizationIds: parseOrganizationIds(ids) }).toLowerCase()
              : kind === 'all'
                ? 'every organization'
                : 'off';
          setNotice(`${targetName} in ${environmentLabel}: ${summary}.`);
          flagsRead.retry();
        }}
      >
        <RadioGroup
          legend="Rollout"
          choices={ROLLOUT_CHOICES}
          value={kind}
          onChange={(value) => setKind(value as RolloutKind)}
        />
        {kind === 'organizations' ? (
          <TextAreaField
            label="Organization IDs"
            value={ids}
            onChange={setIds}
            rows={4}
            error={idsProblem}
            hint="One per line. The organization detail shows its ID."
          />
        ) : null}
        <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
      </WriteDialog>
    );
  }

  let table: ReactNode;
  if (flagsRead.failure) {
    table = (
      <AdminProblem failure={flagsRead.failure} subject="the release flags" onRetry={flagsRead.retry} />
    );
  } else if (!flags || (!catalog && !catalogRead.failure)) {
    table = <LoadingBlock label="Loading the release flags" />;
  } else if (rows.length === 0) {
    table = <p className="admin-muted">The module catalog is empty and no flag is set.</p>;
  } else {
    table = (
      <ScrollTable
        caption={`Release flags in ${environmentLabel}`}
        busy={flagsRead.loading}
        className="admin-table admin-table--flags"
      >
        <thead>
          <tr>
            <th scope="col">Module</th>
            <th scope="col">Product</th>
            <th scope="col">Catalog status</th>
            <th scope="col">Rollout</th>
            <th scope="col">Last change</th>
            {abilities.manageReleaseFlags ? <th scope="col">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.moduleKey}>
              <th scope="row">
                {row.module?.name ?? row.moduleKey}
                <span className="admin-cell-secondary">
                  <code>{row.moduleKey}</code>
                </span>
              </th>
              <td>
                {row.module ? (
                  productName(row.module.productKey)
                ) : (
                  <span className="admin-muted">Not in the catalog</span>
                )}
              </td>
              <td>
                {row.module ? (
                  RELEASE_STATUS_LABELS[row.module.releaseStatus]
                ) : (
                  <span className="admin-muted">Unknown</span>
                )}
              </td>
              <td>
                <RolloutText row={row} />
              </td>
              <td>
                {row.flag ? (
                  <>
                    {row.flag.updatedBy}
                    <span className="admin-cell-secondary">
                      <DateTimeText iso={row.flag.updatedAt} />
                    </span>
                  </>
                ) : (
                  <span className="admin-muted">Never</span>
                )}
              </td>
              {abilities.manageReleaseFlags ? (
                <td>
                  <button
                    type="button"
                    className="admin-action"
                    aria-label={`Change the rollout of ${row.module?.name ?? row.moduleKey}`}
                    onClick={() => open(row)}
                  >
                    Change
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );
  }

  return (
    <AdminPage title="Release flags">
      <PageHeading
        title="Release flags"
        description={`Whether each module is released in ${environmentLabel}, and to whom. Without a flag, a module is released where its catalog status is generally available, and nowhere else.`}
      />
      <SavedNotice message={notice} />
      <PageSection title={`Modules in ${environmentLabel}`}>
        {catalogRead.failure && flags ? (
          <p className="admin-muted">
            The module catalog could not be loaded, so only the modules that have a flag are listed.
          </p>
        ) : null}
        {table}
      </PageSection>
      {elsewhere.length > 0 ? (
        <PageSection
          title="Flags of other environments"
          description="Recorded for another environment; each environment changes its own flags."
        >
          <ul className="admin-plain-list">
            {elsewhere.map((flag) => (
              <li key={`${flag.environment}:${flag.moduleKey}`}>
                <code>{flag.moduleKey}</code> in {ENVIRONMENT_LABELS[flag.environment]}:{' '}
                {describeRollout(flag.rollout)}
              </li>
            ))}
          </ul>
        </PageSection>
      ) : null}
      {dialogView}
    </AdminPage>
  );
}
